import { isMobile } from 'react-device-detect'
import { logException } from 'lib/errors'
import { get } from 'lodash'
import { NETTIX_USER_TYPE } from 'lib/utils/store/currentUser'
import { isDev, isProductionHost } from 'lib/env'
import objectHash from 'object-hash'
import sha256 from 'js-sha256'

const GA_EVENT = {
    ADD_VEHICLE: 'ADD_VEHICLE',
    ADD_VEHICLE_AND_REGISTER: 'ADD_VEHICLE_AND_REGISTER',
    ADD_VEHICLE_FROM_AD: 'ADD_VEHICLE_FROM_AD',
    CREATE_BOS: 'CREATE_BOS',
    BOS_EDIT_OPEN_SECTION: 'BOS_EDIT_OPEN_SECTION',
    BOS_SIGNING_SUCCESS: 'BOS_SIGNING_SUCCESS',
    BOS_PAYMENT_START: 'BOS_PAYMENT_START',
    BOS_PAYMENT_SUCCESS: 'BOS_PAYMENT_SUCCESS',
    LOGIN: 'LOGIN',
    LOGIN_SUCCESS: 'LOGIN_SUCCESS',
    LOGIN_FAILED: 'LOGIN_FAILED',
    REGISTER_SUCCESS: 'REGISTER_SUCCESS',
    REGISTER_FAILED: 'REGISTER_FAILED',
    LOGOUT: 'LOGOUT',
    GOTO_TRAFI_TRANSFERRAL_SERVICE: 'GOTO_TRAFI_TRANSFERRAL_SERVICE',
    OFFER_CREATE: 'OFFER_CREATE',
    OFFER_ACCEPT: 'OFFER_ACCEPT',
    OFFER_DECLINE: 'OFFER_DECLINE',
    ADD_VEHICLE_PRICE_ESTIMATE: 'ADD_VEHICLE_PRICE_ESTIMATE',
    EVENT: 'EVENT',
    RESEND_BUYER_SIGNING_REQUEST: 'RESEND_BUYER_SIGNING_REQUEST',
    CREATE_LATEST_INSPECTION_FROM_TRAFI_DATA: 'CREATE_LATEST_INSPECTION_FROM_TRAFI_DATA',
    BUYER_ACCEPT_FINANCE_OFFER: 'BUYER_ACCEPT_FINANCE_OFFER',
    AD_CREATED: 'AD_CREATED',
    IF_INSURE_AND_REGISTER_YOUR_CAR: 'IF-INSURE-AND-REGISTER-YOUR-CAR',
    VERIFICATION_OF_REGISTRATION: 'VERIFICATION-OF-REGISTRATION',
    SETTLE_PAYMENT: 'SETTLE-PAYMENT',
}

export const GTM_USER_TYPE = {
    PRIVATE: 'private',
    COMPANY: 'company',
}

export const mapNettixUserTypeForGTM = (userType) => {
    switch (userType) {
    case NETTIX_USER_TYPE.SELLER:
        return GTM_USER_TYPE.PRIVATE
    case NETTIX_USER_TYPE.DEALER:
        return GTM_USER_TYPE.COMPANY
    default:
        throw new Error('Invalid nettix user type.')
    }
}

/**
 * Alma viewport breakpoints according to Alma specs.
 * https://github.com/almamedia/datalayer
 * @type {Object}
 */
const ALMA_VIEWPORT_CATEGORIES = Object.freeze({
    0: 'mobile',
    768: 'tablet',
    1024: 'desktop',
    1200: 'desktop-wide'
})

const gtm = {
    userData: {},
    currentUser: {},
    almaUser: {},

    getDataLayer () {
        return window.dataLayer || []
    },

    setCurrentUser (currentUser, almaUser) {
        this.currentUser = currentUser
        this.almaUser = almaUser

        try {
            if (currentUser && currentUser.me) {
                const { id, userType } = currentUser.me
                this.userData = {
                    userId: id,
                    loggedIn: true,
                    accountStatus: 'accountExists',
                    userType: mapNettixUserTypeForGTM(userType),
                }
            } else {
                this.userData = {
                    loggedIn: false,
                }
            }
        } catch (e) {
            logException(e)
        }
    },

    mapBosData (bos) {
        try {
            if (bos) {
                const role = bos.currentUserRole
                const accountStatus = role === 'SELLER' ? 'accountExists' : (
                    bos.buyer.isRegistered ? 'accountExists' : 'noAccount'
                )

                return {
                    bosId: bos.id,
                    bosStatus: bos.status,
                    bosUserRole: bos.currentUserRole,
                    accountStatus: accountStatus,
                }
            } else {
                return {}
            }
        } catch (e) {
            logException(e)
        }
    },

    pushPageView (pathname, data = {}) {
        this.push({
            event: 'pageView',
            pageName: pathname,
            ...this.userData,
            ...data,
        })
    },

    pushGaEvent (eventName, data = {}) {
        this.push({
            event: 'gaEvent',
            eventName: eventName,
            ...this.userData,
            ...data,
        })
    },

    getAlmaViewportCategory (windowWidth) {
        const entries = Object.entries(ALMA_VIEWPORT_CATEGORIES)

        const index = entries.reverse().findIndex((entry) => {
            const entryStartWidth = entry[0]
            return windowWidth >= entryStartWidth
        })

        return entries[index][1]
    },

    pushAlmaDigitalData (pageType, primaryCategory) {
        try {
            // Do not push if path did not change from previous push.
            // This is the case when user navigates to modal and only hash (#) part changes
            if (!document.location || get(window, 'digitalData.page.pageInfo.locationPathName') === document.location.pathname) {
                return
            }

            const env = isProductionHost()
                ? 'prod'
                : isDev()
                    ? 'dev'
                    : 'test'
            const pageId = objectHash(document.location.pathname)
            const windowWidth = window.innerWidth

            const data = {
                digitalData: {
                    pageInstanceID: `${pageId}_one_${env}`,
                    version: {
                        almaVersion: '2.3',
                        specificationVersion: '1.0',
                    },
                    page: {
                        pageInfo: {
                            pageID: pageId,
                            sysEnv: isMobile ? 'mobile' : 'desktop',
                            pageTitle: document.title,
                            publisher: 'Oma Nettix',
                            locationProtocol: document.location.protocol.slice(0, -1),
                            locationHost: document.location.host,
                            locationPathName: document.location.pathname,
                        },
                        category: {
                            primaryCategory: primaryCategory,
                            pageType: pageType,
                        },
                        attributes: {
                            environment: env,
                            viewport: this.getAlmaViewportCategory(windowWidth),
                            width: windowWidth,
                            userAgentHeader: navigator.userAgent,
                            contentCharge: 'free',
                        },
                    },
                    user: [
                        {
                            segment: {
                                loginstatus: this.userData.loggedIn ? 'loggedin' : 'loggedout',
                            },
                        }
                    ],
                }
            }

            // Some dealer type users do not have email
            const userId = get(this.currentUser, 'me.id', null)
            const email = get(this.currentUser, 'me.email', null)
            const userType = get(this.currentUser, 'me.userType', null)
            const almaIdHash = get(this.almaUser, 'almaIdHash', null)

            if (this.userData.loggedIn && email) {
                data.digitalData.user[0] = {
                    ...data.digitalData.user[0],
                    profile: [
                        {
                            profileInfo: {
                                userHash: sha256(userId),
                                cdime: sha256(email.toLowerCase().trim()),
                                almaIDHash: almaIdHash,
                                accountType: mapNettixUserTypeForGTM(userType),
                            },
                        },
                    ],
                }
            }

            window.digitalData = data.digitalData

            this.push(data)
        } catch (e) {
            logException(e)
        }
    },

    push (data) {
        this.getDataLayer().push(data)
    },
}

export {
    gtm,
    GA_EVENT,
}
